import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import { CryptoAlertTile } from "@/components/CryptoAlertTile";
import { TabSelector } from "@/components/TabSelector";
import {
  useInfiniteListings,
  useInfiniteDelistings,
} from "@/hooks/useCryptoAlerts";
import {
  CryptoAlertData,
  PositionData,
  ClosedPositionData,
  AlertTier,
} from "@crypto-trading/common/browser";
import { Loader2, Brain } from "lucide-react";
import { ReferralBottomNav } from "@/components/ReferralBottomNav";
import { PositionDetailDrawer } from "@/components/PositionDetailDrawer";
import { AlertDetailDrawer } from "@/components/AlertDetailDrawer";
import { PositionTradeDrawer } from "@/components/PositionTradeDrawer";
import { ensureUsdtSymbol } from "@/utils/symbols";
import { TierSelector } from "@/components/TierSelector";
import { trackEvent } from "@/utils/analytics";

const ALERTS_PER_PAGE = 3;

/**
 * Mobile version of the Alerts page - maintains original mobile-optimized design
 */
export const AlertsPageMobile: React.FC = () => {
  const navigate = useNavigate();
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString() || "";

  const [activeTab, setActiveTab] = useState<"listings" | "delistings">(
    "listings"
  );
  const [selectedPosition, setSelectedPosition] = useState<
    PositionData | ClosedPositionData | null
  >(null);
  const [selectedAlert, setSelectedAlert] = useState<CryptoAlertData | null>(
    null
  );
  const [isTradeDrawerOpen, setIsTradeDrawerOpen] = useState(false);
  const [tradeSymbol, setTradeSymbol] = useState<string | null>(null);
  const [tierFilter, setTierFilter] = useState<"ALL" | AlertTier>(
    AlertTier.TIER_1
  );

  const loadMoreRef = useRef<HTMLDivElement>(null);
  const tierFilterValue = tierFilter === "ALL" ? undefined : tierFilter;
  const tierOptions = [
    { value: "ALL" as const, label: "All" },
    { value: AlertTier.TIER_1 as const, label: "Tier 🔥🔥🔥" },
    { value: AlertTier.TIER_2 as const, label: "Tier 🔥🔥" },
    { value: AlertTier.TIER_3 as const, label: "Tier 🔥" },
  ];

  // React Query hooks
  const {
    data: listingsData,
    isLoading: isLoadingListings,
    fetchNextPage: fetchNextListings,
    hasNextPage: hasNextListings,
    isFetchingNextPage: isFetchingNextListings,
  } = useInfiniteListings(
    userId,
    ALERTS_PER_PAGE,
    activeTab === "listings",
    tierFilterValue
  );

  const {
    data: delistingsData,
    isLoading: isLoadingDelistings,
    fetchNextPage: fetchNextDelistings,
    hasNextPage: hasNextDelistings,
    isFetchingNextPage: isFetchingNextDelistings,
  } = useInfiniteDelistings(
    userId,
    ALERTS_PER_PAGE,
    activeTab === "delistings",
    tierFilterValue
  );

  // Flatten alerts from all pages
  const alerts =
    activeTab === "listings"
      ? (listingsData?.pages.flatMap((page) => page.alerts) ?? [])
      : (delistingsData?.pages.flatMap((page) => page.alerts) ?? []);

  const isLoading =
    activeTab === "listings" ? isLoadingListings : isLoadingDelistings;
  const hasMore =
    activeTab === "listings"
      ? (hasNextListings ?? false)
      : (hasNextDelistings ?? false);
  const isFetchingNextPage =
    activeTab === "listings"
      ? isFetchingNextListings
      : isFetchingNextDelistings;
  const fetchNextPage =
    activeTab === "listings" ? fetchNextListings : fetchNextDelistings;

  const handleViewPositionFromAlert = (positionId: string) => {
    // Find the alert with this position
    const alert = alerts.find((a) => a.userPosition?.id === positionId);
    if (alert?.userPosition) {
      // Convert UserPosition to PositionData format
      const positionData: PositionData = {
        positionId: alert.userPosition.id,
        symbol: alert.userPosition.symbol,
        positionType: alert.userPosition.positionType as "LONG" | "SHORT",
        amount: alert.userPosition.amount.toString(),
        leverage: alert.userPosition.leverage.toString(),
        entryPrice: alert.userPosition.entryPrice.toString(),
        markPrice: alert.userPosition.markPrice?.toString() || "0",
        exitPrice: alert.userPosition.exitPrice?.toString() || "0",
        pnl: alert.userPosition.realizedPnl?.toString() || "0",
        pnlPercentage: alert.userPosition.pnlPercentage?.toString() || "0",
        exchangeCode: alert.exchange,
        exchangeName: alert.exchange,
        usdAmount: alert.userPosition.usdAmount.toString(),
        openedAt: alert.userPosition.openedAt?.toString() || undefined,
        closedAt: alert.userPosition.closedAt?.toString() || undefined,
      };
      setSelectedPosition(positionData);
    }
  };

  const handleOpenTradeDrawer = (alert: CryptoAlertData) => {
    setTradeSymbol(alert.currency ? ensureUsdtSymbol(alert.currency) : null);
    setIsTradeDrawerOpen(true);
  };

  const handleOpenAnalyzePage = () => {
    trackEvent("custom_trade_attempt", { page: "alerts", userId });
    navigate("/analyze");
  };

  useEffect(() => {
    if (selectedAlert) {
      trackEvent("alert_drawer_view", {
        userId,
        alertId: selectedAlert.id,
        symbol: selectedAlert.currency,
        alertType: selectedAlert.type,
        tier: selectedAlert.tier ?? null,
        exchange: selectedAlert.exchange,
      });
    }
  }, [selectedAlert?.id]);

  // Infinite scroll observer
  useEffect(() => {
    if (!hasMore || isLoading || isFetchingNextPage) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMore, isLoading, isFetchingNextPage, activeTab, fetchNextPage]);

  return (
    <div className="min-h-screen bg-primary flex flex-col">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-2xl mx-auto">
          {/* Tab Toggle */}
          <TabSelector
            options={[
              { value: "listings", label: "Listings" },
              { value: "delistings", label: "Delistings" },
            ]}
            value={activeTab}
            onChange={setActiveTab}
          />

          <div className="my-4">
            <TierSelector<typeof tierFilter>
              options={tierOptions}
              value={tierFilter}
              onChange={(value) => setTierFilter(value)}
            />
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-accent" />
            </div>
          )}

          {/* Alerts List */}
          {!isLoading && alerts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-text-secondary">No {activeTab} found</p>
            </div>
          )}

          {!isLoading && (
            <div className="space-y-3">
              {alerts.map((alert) => (
                <CryptoAlertTile
                  key={alert.id}
                  alert={alert}
                  onViewPosition={handleViewPositionFromAlert}
                  onDetailsClick={() => setSelectedAlert(alert)}
                  onTrade={handleOpenTradeDrawer}
                />
              ))}
            </div>
          )}

          {/* Load More Trigger */}
          {!isLoading && hasMore && (
            <div ref={loadMoreRef} className="flex justify-center py-8">
              {isFetchingNextPage && (
                <Loader2 className="w-6 h-6 animate-spin text-accent" />
              )}
            </div>
          )}

          {/* End of List */}
          {!isLoading && !hasMore && alerts.length > 0 && (
            <div className="text-center py-8">
              <p className="text-text-secondary text-sm">
                No more {activeTab} to load
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Alert Detail Drawer */}
      <AlertDetailDrawer
        userId={userId}
        alert={selectedAlert}
        open={!!selectedAlert}
        onOpenChange={(open) => !open && setSelectedAlert(null)}
        onTrade={(alert) => {
          setTradeSymbol(
            alert.currency ? ensureUsdtSymbol(alert.currency) : null
          );
          setIsTradeDrawerOpen(true);
        }}
      />

      {/* Position Detail Drawer */}
      <PositionDetailDrawer
        userId={userId}
        position={selectedPosition}
        open={!!selectedPosition}
        onOpenChange={(open) => !open && setSelectedPosition(null)}
        hideCloseAction={true}
        source="alerts_page"
      />

      <PositionTradeDrawer
        userId={userId}
        symbol={tradeSymbol}
        open={isTradeDrawerOpen}
        onOpenChange={(open) => {
          setIsTradeDrawerOpen(open);
          if (!open) {
            setTradeSymbol(null);
          }
        }}
        onGoToPosition={(position) => {
          if (position) {
            setSelectedPosition(position);
          }
        }}
      />

      {/* Floating Action Button - Navigate to Analyze page */}
      <button
        onClick={handleOpenAnalyzePage}
        className="fixed bottom-24 right-4 z-40 w-14 h-14 rounded-full bg-accent hover:bg-accent/80 flex items-center justify-center shadow-lg transition-all hover:scale-105 active:scale-95"
        aria-label="Analyze token"
      >
        <Brain className="w-6 h-6 text-white" />
      </button>

      <ReferralBottomNav />
    </div>
  );
};
